import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { sendAhoyAndGAEvents } from 'dpl/util/analytics';
import postSignupRedirect from 'dpl/util/postSignupRedirect';
import { USER_TYPES } from 'dpl/constants/shared';
import { TOS_PATH, PRIVACY_PATH } from 'dpl/constants/urls';
import { EVENT_CATEGORIES } from 'dpl/constants/analytics';
import ErrorWrapper from 'dpl/form/components/ErrorWrapper';
import GenericSelect from 'dpl/components/GenericSelect';
import TelephoneInput from 'dpl/form/components/TelephoneInput';

const REFERRAL_DESCRIPTION_OPTIONS = [
  'Another breeder',
  'One of my buyers',
  'Facebook',
  'Google',
  'Email',
  'Avidog',
  'TV',
  'Radio',
  'Other'
].map(v => ({ title: v, value: v }));

export default function SignupForm({
  buttonClassName,
  buttonText,
  formState,
  handleFormFieldChange,
  isFormSubmitting,
  countriesData,
  userType,
  formErrors,
  selectedCountry,
  referredBy,
  handleFormSubmission,
  didFormSubmissionFail,
  onAuth
}) {
  function handleSubmit(e) {
    e.preventDefault();

    handleFormSubmission({
      user: { referred_by: referredBy },
      userType
    })
      .then((json) => {
        if (json.response.ok) {
          if (userType === USER_TYPES.BREEDER) {
            sendAhoyAndGAEvents('submit', {
              event_category: EVENT_CATEGORIES.BREEDER_SIGNUP
            });
          }

          if (onAuth) {
            return onAuth(json);
          }

          return postSignupRedirect(userType);
        }
        return null;
      });
  }

  const isBreeder = userType === USER_TYPES.BREEDER;

  const countriesOptions = countriesData.value.data.map(c => ({ title: c.name, value: c.id }));

  return (
    <div className={ classnames('SignupForm', { BreederSignupForm: isBreeder }) }>
      <form onSubmit={ handleSubmit }>
        <div className="row mb6">
          <div className="col">
            <label htmlFor="first_name">
              <p className="dark-gray mb1 f2 fw5">First Name</p>
              <ErrorWrapper
                errors={ formErrors.first_name }
                isShown={ didFormSubmissionFail }
              >
                <input
                  name="first_name"
                  className="w-100 br1 ba b--light-gray ph2 pv2"
                  value={ formState.first_name || '' }
                  onChange={ handleFormFieldChange }
                  placeholder="Jane"
                />
              </ErrorWrapper>
            </label>
          </div>
          <div className="col">
            <label htmlFor="last_name">
              <p className="dark-gray mb1 f2 fw5">Last Name</p>
              <input
                name="last_name"
                className="w-100 br1 ba b--light-gray ph2 pv2"
                value={ formState.last_name || '' }
                onChange={ handleFormFieldChange }
                placeholder="Smith"
              />
            </label>
          </div>
        </div>

        { isBreeder && (
          <Fragment>
            <div className="row mb6">
              <div className="col">
                <label htmlFor="program_name">
                  <p className="dark-gray mb1 f2 fw5">Program Name</p>
                  <ErrorWrapper
                    errors={ formErrors.program_name }
                    isShown={ didFormSubmissionFail }
                  >
                    <input
                      name="program_name"
                      className="w-100 br1 ba b--light-gray ph2 pv2"
                      value={ formState.program_name || '' }
                      onChange={ handleFormFieldChange }
                      placeholder="e.g. High Peaks Farm"
                    />
                  </ErrorWrapper>
                </label>
              </div>
            </div>
            <div className="row mb6">
              <div className="col">
                <label htmlFor="country_id">
                  <p className="dark-gray mb1 f2 fw5">Country</p>
                  <ErrorWrapper
                    errors={ formErrors.country_id }
                    isShown={ didFormSubmissionFail }
                  >
                    <GenericSelect
                      name="country_id"
                      onChange={ handleFormFieldChange }
                      options={ countriesOptions }
                      value={ formState.country_id }
                    />
                  </ErrorWrapper>
                </label>
              </div>
            </div>
            { selectedCountry.allows_phone_number && (
              <div className="row mb6">
                <div className="col">
                  <TelephoneInput
                    displayErrors={ didFormSubmissionFail }
                    fullWidth
                    label="Phone Number"
                    name="phone_number"
                  />
                </div>
              </div>
            ) }
          </Fragment>
        ) }

        <div className="mb6">
          <label htmlFor="email">
            <p className="dark-gray mb1 f2 fw5">Email</p>
            <ErrorWrapper
              errors={ formErrors.email }
              isShown={ didFormSubmissionFail }
            >
              <input
                name="email"
                autoCapitalize="none"
                className="w-100 br1 ba b--light-gray ph2 pv2"
                value={ formState.email || '' }
                onChange={ handleFormFieldChange }
                placeholder="jane@highpeaksfarm.com"
              />
            </ErrorWrapper>
          </label>
        </div>

        <div className="mb6">
          <label htmlFor="password">
            <p className="dark-gray mb1 f2 fw5">Password</p>
            <ErrorWrapper
              errors={ formErrors.password }
              isShown={ didFormSubmissionFail }
            >
              <input
                name="password"
                type="password"
                autoCapitalize="none"
                className="w-100 br1 ba b--light-gray ph2 pv2"
                value={ formState.password || '' }
                onChange={ handleFormFieldChange }
                placeholder="6+ characters"
              />
            </ErrorWrapper>
          </label>
        </div>

        <div className="mb6">
          <button
            data-test-id="signup_submit"
            type="submit"
            disabled={ isFormSubmitting }
            className={
              classnames(
                buttonClassName,
                { 'button--loading': isFormSubmitting }
              )
            }
          >
            { buttonText }
          </button>
        </div>

        <div className="mb6">
          <p className="f2 mid-gray tc">
              By clicking &quot;{ buttonText }&quot; I agree to Good Dog&apos;s&nbsp;
            { isBreeder && (
              <Fragment>
                <a className="underline" href="/breeder-code-of-ethics">Breeder Code of Ethics</a>,&nbsp;
              </Fragment>
            ) }
            <a className="underline" href={ TOS_PATH }>Terms of Service</a>
              &nbsp;and <a className="underline" href={ PRIVACY_PATH }>Privacy Policy</a>.
          </p>
        </div>
      </form>
    </div>
  );
}


SignupForm.propTypes = {
  formState: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    phone_number: PropTypes.string,
    password: PropTypes.string
  }).isRequired,
  formErrors: PropTypes.object.isRequired,
  didFormSubmissionFail: PropTypes.bool.isRequired,
  handleFormFieldChange: PropTypes.func.isRequired,
  handleFormSubmission: PropTypes.func.isRequired,
  isFormSubmitting: PropTypes.bool.isRequired,
  userType: PropTypes.oneOf(Object.values(USER_TYPES)).isRequired,
  onAuth: PropTypes.func,
  buttonText: PropTypes.string,
  buttonClassName: PropTypes.string,
  referredBy: PropTypes.string,
  countriesData: PropTypes.shape({
    value: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      })).isRequired
    }).isRequired
  }).isRequired,
  selectedCountry: PropTypes.shape({
    allows_phone_number: PropTypes.bool
  })
};

SignupForm.defaultProps = {
  onAuth: null,
  buttonText: 'Continue',
  buttonClassName: 'w-100 button button--primary',
  referredBy: null,
  selectedCountry: {}
};
